import React, { Fragment, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { X, Loader2 } from 'lucide-react';
import { getInitials } from 'utils/getInitials';
import { formatIsoToTime } from 'utils/date';
import {
  useRegularizationRequestQuery,
  useActionRegularizationRequestMutation,
} from 'service/hrms/hooks/useAttendanceHooks';

export interface MissedCheckoutData {
  requestUuid?: string | null;
  employeeName?: string;
  employeeInitials?: string;
  employeeAvatarUrl?: string | null;
  department?: string;
  requestType?: string;
  dateText?: string;
  systemCheckIn?: string;
  reportedCheckOut?: string;
  reason?: string;
  submittedAt?: string;
  requestId?: string;
  canReview?: boolean;
}

interface MissedCheckoutReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestUuid?: string | null;
  data?: MissedCheckoutData | null;
  onApprove?: () => void;
  onReject?: () => void;
}


// Dates here have no meaningful "time" component, but the fallback
// (createdAt) does — so still honor the employee's attendance timezone to
// avoid the date rolling to a different day for the reviewing admin.
const formatDate = (dateStr?: string | null, timeZone?: string | null): string => {
  if (!dateStr) return 'Thursday, August 6, 2026';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    });
  } catch {
    return dateStr;
  }
};

const MissedCheckoutReviewModal: React.FC<MissedCheckoutReviewModalProps> = ({
  isOpen,
  onClose,
  requestUuid: propRequestUuid,
  data,
  onApprove,
  onReject,
}) => {
  const [remarks, setRemarks] = useState('');
  // Tracks which specific action is in flight so only the clicked button
  // shows a spinner — actionMutation.isPending alone is shared by both
  // buttons and would spin both of them regardless of which was clicked.
  const [pendingAction, setPendingAction] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const targetRequestUuid = propRequestUuid || data?.requestUuid;
  const {
    data: apiResponse,
    isLoading: isFetchingApi,
    isError: isApiError,
  } = useRegularizationRequestQuery(targetRequestUuid || undefined);

  const actionMutation = useActionRegularizationRequestMutation();

  // Combine default, prop data, and live API data
  const liveData: MissedCheckoutData = apiResponse
    ? {
      requestUuid: apiResponse.requestUuid,
      employeeName: apiResponse.employeeName,
      employeeInitials: getInitials(apiResponse.employeeName),
      employeeAvatarUrl: apiResponse.profileImageUrl || null,
      department: apiResponse.designation || apiResponse.department || 'Product',
      requestType: 'Missed check-out request',
      dateText: formatDate(apiResponse.attendanceDate || apiResponse.createdAt, apiResponse.attendanceTimezone),
      systemCheckIn: formatIsoToTime(apiResponse.systemCheckInTime, apiResponse.attendanceTimezone),
      reportedCheckOut: formatIsoToTime(apiResponse.requestedCheckOutTime, apiResponse.attendanceTimezone),
      reason: apiResponse.reason || 'No reason provided',
      submittedAt: apiResponse.createdAt
        ? `Submitted ${new Date(apiResponse.createdAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
        : 'Submitted recently',
      requestId: `Request ID #${apiResponse.requestUuid.slice(0, 8).toUpperCase()}`,
      canReview: apiResponse.canReview ?? false,
    }
    : { ...data };

  const modalData = liveData;
  const canReview = modalData.canReview ?? true;

  const handleAction = (action: 'APPROVED' | 'REJECTED') => {
    if (targetRequestUuid) {
      setPendingAction(action);
      actionMutation.mutate(
        { requestUuid: targetRequestUuid, payload: { action, remarks: remarks || null } },
        {
          onSuccess: () => {
            if (action === 'APPROVED' && onApprove) onApprove();
            if (action === 'REJECTED' && onReject) onReject();
            onClose();
          },
          onSettled: () => setPendingAction(null),
        }
      );
    } else {
      if (action === 'APPROVED' && onApprove) onApprove();
      if (action === 'REJECTED' && onReject) onReject();
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75 backdrop-blur-xs" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[480px] transform overflow-hidden rounded-2xl bg-[#141824] border border-[#222838] p-6 text-left align-middle shadow-2xl transition-all space-y-5">
                {isFetchingApi ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-3">
                    <Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" />
                    <p className="text-slate-400 text-sm">Loading regularization request...</p>
                  </div>
                ) : isApiError ? (
                  <div className="py-6 text-center space-y-3">
                    <p className="text-red-400 text-sm">Failed to load regularization request details.</p>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-xs font-semibold text-slate-300 bg-[#222836] rounded-lg"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3.5">
                        {modalData.employeeAvatarUrl ? (
                          <img
                            src={modalData.employeeAvatarUrl}
                            alt={modalData.employeeName}
                            className="w-12 h-12 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#8B5CF6] flex items-center justify-center text-white font-semibold text-base shrink-0 shadow-sm">
                            {modalData.employeeInitials || 'SJ'}
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-bold text-lg sm:text-[19px] leading-tight">
                            {modalData.employeeName}
                          </h3>
                          <p className="text-[#8E99A8] text-xs sm:text-sm font-normal mt-0.5">
                            {modalData.department} · {modalData.requestType}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={onClose}
                        type="button"
                        className="w-8 h-8 rounded-lg bg-[#222836] hover:bg-[#2C3446] text-[#8E99A8] hover:text-white flex items-center justify-center transition-colors shrink-0"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Status Banner */}
                    {(() => {
                      const statusStr = (apiResponse?.status as string)?.toUpperCase();
                      const closureReason = apiResponse?.closureReason;
                      let bannerText = `Awaiting your decision — for ${modalData.dateText}`;
                      let bannerClass = 'bg-[#261E14] border-[#523A1E] text-[#EAA846]';

                      if (statusStr === 'APPROVED') {
                        bannerText = `Approved — for ${modalData.dateText}`;
                        bannerClass = 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400';
                      } else if (statusStr === 'REJECTED') {
                        bannerText = `Rejected — for ${modalData.dateText}`;
                        bannerClass = 'bg-red-950/40 border-red-500/30 text-red-400';
                      } else if (statusStr === 'RECORDED') {
                        bannerText = `Recorded — no approval required — for ${modalData.dateText}`;
                        bannerClass = 'bg-slate-800/40 border-slate-700 text-slate-300';
                      } else if (statusStr === 'CANCELLED') {
                        bannerClass = 'bg-rose-950/40 border-rose-500/30 text-rose-300';
                        if (closureReason === 'SUPERSEDED_BY_ATTENDANCE_CORRECTION') {
                          bannerText = `Closed — attendance corrected directly — for ${modalData.dateText}`;
                        } else if (closureReason === 'REQUESTER_DEACTIVATED') {
                          bannerText = `Closed — employee is no longer active — for ${modalData.dateText}`;
                        } else {
                          bannerText = `Closed — for ${modalData.dateText}`;
                        }
                      } else if (!canReview) {
                        bannerText = `Pending review — for ${modalData.dateText}`;
                        bannerClass = 'bg-slate-800/40 border-slate-700 text-slate-300';
                      }

                      return (
                        <div className={`w-full rounded-xl px-4 py-3 text-center sm:text-left border ${bannerClass}`}>
                          <p className="font-medium text-xs sm:text-sm leading-snug">{bannerText}</p>
                        </div>
                      );
                    })()}

                    {/* System check-in & Employee-reported check-out */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          System check-in
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl px-4 py-3 text-white font-medium text-sm sm:text-base">
                          {modalData.systemCheckIn}
                        </div>
                      </div>

                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Employee-reported check-out
                        </label>
                        <div className="w-full bg-[#201C2E] border border-[#3B3252] rounded-xl px-4 py-3 text-[#A78BFA] font-semibold text-sm sm:text-base">
                          {modalData.reportedCheckOut}
                        </div>
                      </div>
                    </div>

                    {/* Current Applied Check-Out (if available) */}
                    {apiResponse?.effectiveCheckOutTime && (
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Current check-out
                        </label>
                        <div className="w-full bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl px-4 py-3 text-[#10B981] font-semibold text-sm sm:text-base">
                          {formatIsoToTime(apiResponse.effectiveCheckOutTime, apiResponse.attendanceTimezone)}
                        </div>
                      </div>
                    )}

                    {/* Reason */}
                    <div>
                      <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                        Reason from {modalData.employeeName?.split(' ')[0] || 'Employee'}
                      </label>
                      <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl p-4 text-[#D1D5DB] text-xs sm:text-sm leading-relaxed font-normal min-h-[86px]">
                        {modalData.reason?.startsWith('"') ? modalData.reason : `"${modalData.reason}"`}
                      </div>
                    </div>

                    {/* Employee Notes (if non-empty) */}
                    {apiResponse?.notes && (
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Additional Notes
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl p-3.5 text-[#D1D5DB] text-xs sm:text-sm leading-relaxed">
                          {apiResponse.notes}
                        </div>
                      </div>
                    )}

                    {/* Display existing Reviewer Remarks if available */}
                    {apiResponse?.reviewerRemarks && (
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Reviewer Remarks {apiResponse.reviewedByName ? `(by ${apiResponse.reviewedByName})` : ''}
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl p-3.5 text-[#D1D5DB] text-xs sm:text-sm leading-relaxed">
                          {apiResponse.reviewerRemarks}
                        </div>
                      </div>
                    )}

                    {/* Optional Remarks input for Reviewer when canReview is true */}
                    {canReview && (
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="text-[#8E99A8] text-xs font-medium block">
                            Reviewer Remarks <span className="text-slate-500">(Optional)</span>
                          </label>
                          <span className="text-[11px] text-slate-500">
                            {remarks.length}/1000
                          </span>
                        </div>
                        <textarea
                          rows={2}
                          maxLength={1000}
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Add comments or feedback for this decision (up to 1000 characters)..."
                          className="w-full bg-[#1A1F2C] border border-[#272E3F] focus:border-[#8B5CF6] rounded-xl px-3.5 py-2.5 text-white text-xs sm:text-sm outline-none transition-colors resize-none"
                        />
                      </div>
                    )}

                    {/* Metadata Footer Row */}
                    <div className="flex items-center justify-between text-xs text-[#717D90] font-normal px-0.5 pt-0.5">
                      <span>{modalData.submittedAt}</span>
                      <span>{modalData.requestId}</span>
                    </div>

                    {/* Action Buttons */}
                    {canReview ? (
                      <div className="grid grid-cols-2 gap-3.5 pt-1">
                        <button
                          type="button"
                          disabled={actionMutation.isPending}
                          onClick={() => handleAction('REJECTED')}
                          className="w-full bg-[#351C24] hover:bg-[#45202D] border border-[#5C2735] text-[#F87171] font-semibold py-3 px-4 rounded-xl transition-all active:scale-[0.98] text-sm sm:text-base flex items-center justify-center cursor-pointer disabled:opacity-50"
                        >
                          {pendingAction === 'REJECTED' ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            'Reject'
                          )}
                        </button>

                        <button
                          type="button"
                          disabled={actionMutation.isPending}
                          onClick={() => handleAction('APPROVED')}
                          className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold py-3 px-4 rounded-xl transition-all active:scale-[0.98] text-sm sm:text-base flex items-center justify-center cursor-pointer shadow-lg shadow-emerald-950/30 disabled:opacity-50"
                        >
                          {pendingAction === 'APPROVED' ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            'Approve check-out'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={onClose}
                          className="w-full bg-[#1A1F2C] hover:bg-[#232938] border border-[#272E3F] text-slate-300 font-semibold py-3 px-4 rounded-xl transition-all text-sm sm:text-base flex items-center justify-center"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MissedCheckoutReviewModal;
